import React, { useState, useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';
import { useDrop } from 'react-dnd';
import { ListItemIcon, Menu, MenuItem } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { RemoveCircleOutline } from '@material-ui/icons';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import i18n from 'i18n-js';

import { default as ordersStore } from 'store/ordersStore';
import SectionHeader from './components/SectionHeader';
import SectionCard from './components/SectionCard';
import { useStyles } from './styles';
import OrdersListTable from 'components/orders/ordersList/OrdersListTable';
import { OrderItem, OrderDetails } from 'store/ordersStore/reducer';
import { CookColumnSort, MenuType, OrderItemStatus, Positions, PrintType } from 'constants/enums';
import { useAppSelector } from 'index';
import { CategoryDetails } from 'store/categoryStore/reducer';
import { allItem } from 'components/orders/ordersList/WorkAreaSelector';
import categoryStore from 'store/categoryStore';
import { getItemStatusText } from 'helpers/helperFunctions';
import { LocalStorageKeys } from 'constants/localStorageKeys';
import menusStore from 'store/menusStore';
import CookBoardSkeleton from './components/CookBoardSkeleton';

const columnsWidth = 33;

export default function CookView() {
    const dispatch = useDispatch();
    const classes = useStyles();
    const menus = useAppSelector((state) => state.menus.menus);
    const positionOrdersRedux = useAppSelector((state) => state.orders.positionOrders);
    const allOrdersRedux = useAppSelector((state) => state.orders.orders);
    const positionOrdersLoaded = useAppSelector((state) => state.orders.positionOrdersLoaded);
    const categories = useAppSelector(
        (state) => state.category.categoryDetails?.categories
    )?.filter((item) => !item.isDisabled && item.position === Positions.COOK);
    const selectedWorkAreas = useAppSelector((state) => state.category.cookCategories);

    const [hideDone, setHideDone] = useState(false);
    const [sorting, setSorting] = useState<[CookColumnSort, CookColumnSort, CookColumnSort]>([
        CookColumnSort.Course,
        CookColumnSort.Course,
        CookColumnSort.Course,
    ]);
    const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
    const [, reRender] = useState(false);
    const positionOrders = useRef<OrderItem[]>([]);
    const [printItem, setPrintItem] = useState<null | OrderItem>(null);
    const [printAnchorEl, setPrintAnchorEl] = useState<null | HTMLElement>(null);

    function handlePrintClick(event: React.MouseEvent<HTMLButtonElement>, item: OrderItem) {
        setPrintAnchorEl(event.currentTarget ?? event.target);
        setPrintItem(item);
    }

    function handlePrintClose() {
        setPrintAnchorEl(null);
        setPrintItem(null);
    }

    const handlePrint = (printType: PrintType) => {
        const orderId =
            printItem?.orderId ??
            allOrdersRedux.find((o) => o.orderItems.some((oi) => oi.id === printItem!.id))?.id;
        const workAreaIds = selectedWorkAreas.map((wa) => wa.id);
        if (orderId && printType === PrintType.WholeOrder) {
            dispatch(ordersStore.actionCreators.PrintOrder(orderId));
        } else if (orderId && printType === PrintType.SingleItem) {
            const itemId =
                printItem!.uniqueId && printItem!.uniqueId?.length > 7
                    ? printItem!.uniqueId
                    : printItem!.id;
            dispatch(
                ordersStore.actionCreators.PrintOrder(orderId, [itemId!], printItem?.dailyMenuId)
            );
        } else if (printType === PrintType.AllInWorkArea) {
            const workAreaId = printItem?.categoryId;
            let ordersIds;
            if (selectedWorkAreas.length) {
                ordersIds = allOrdersRedux
                    .filter((o) =>
                        o.orderItems.some(
                            (oi) =>
                                workAreaIds.includes(oi.categoryId!) ||
                                oi.dishes.some((d) => workAreaIds.includes(d.categoryId!))
                        )
                    )
                    .filter((o) => selectedOrders.includes(o.id))
                    .map((o) => o.id);
            }

            dispatch(
                ordersStore.actionCreators.PrintMultipleOrders(
                    ordersIds,
                    undefined,
                    [workAreaId!],
                    Positions.COOK
                )
            );
        } else if (printType === PrintType.AllInStatus) {
            let ordersIds;
            if (selectedWorkAreas.length) {
                const workAreaIds = selectedWorkAreas.map((wa) => wa.id);
                ordersIds = allOrdersRedux
                    .filter((o) =>
                        o.orderItems.some(
                            (oi) =>
                                workAreaIds.includes(oi.categoryId!) ||
                                oi.dishes.some((d) => workAreaIds.includes(d.categoryId!))
                        )
                    )
                    .filter((o) => selectedOrders.includes(o.id))
                    .map((o) => o.id);
            }

            dispatch(
                ordersStore.actionCreators.PrintMultipleOrders(
                    ordersIds,
                    printItem?.status,
                    workAreaIds,
                    Positions.COOK
                )
            );
        }
        handlePrintClose();
    };

    const [{ isOver1 }, drop1] = useDrop(() => ({
        accept: 'a',
        canDrop: () => true,
        collect: (monitor) => ({
            isOver1: monitor.isOver(),
        }),
        drop: (item: OrderItem) => {
            changeOrderStatus(item, OrderItemStatus.Order);
        },
    }));
    const [{ isOver2 }, drop2] = useDrop(() => ({
        accept: 'a',
        canDrop: () => true,
        collect: (monitor) => ({
            isOver2: monitor.isOver(),
        }),
        drop: (item: OrderItem) => {
            changeOrderStatus(item, OrderItemStatus.InProgress);
        },
    }));
    const [{ isOver3 }, drop3] = useDrop(() => ({
        accept: 'a',
        canDrop: () => true,
        collect: (monitor) => ({
            isOver3: monitor.isOver(),
        }),
        drop: (item: OrderItem) => {
            changeOrderStatus(item, OrderItemStatus.Ready);
        },
    }));

    useEffect(() => {
        checkHide();
    }, []);

    function getUniquePositionOrders() {
        const newList: OrderItem[] = [];
        if (positionOrders.current.length === 0) {
            newList.push(
                ...positionOrdersRedux.map((each) => ({
                    ...each,
                    uniqueId: !each.dailyMenuId
                        ? each.uniqueId
                        : (Math.random() + 1).toString(36).substring(7),
                }))
            );
        } else {
            positionOrdersRedux.forEach((each) => {
                newList.push({
                    ...each,
                    uniqueId: !each.dailyMenuId
                        ? each.uniqueId
                        : (Math.random() + 1).toString(36).substring(7),
                });
            });
        }

        return newList;
    }

    function checkHide() {
        const isHidden = localStorage.getItem(LocalStorageKeys.hideDone);
        if (isHidden !== null) {
            setHideDone(isHidden === 'true');
        }
    }

    function getOrdersList(status: OrderItemStatus): OrderItem[] {
        let index = 0;
        switch (status) {
            case OrderItemStatus.Order:
            case OrderItemStatus.ReadyToPrepare:
                index = 0;
                break;
            case OrderItemStatus.InProgress:
                index = 1;
                break;
            case OrderItemStatus.Ready:
                index = 2;
                break;
            default:
                break;
        }
        const filteredAndSorted = positionOrders.current
            .filter((each: OrderItem) => selectedWorkAreas.some((i) => i.id === each.categoryId))
            .filter((each: OrderItem) => selectedOrders.includes(each.orderId!))
            .filter(
                (each: OrderItem) =>
                    each.status === status ||
                    (status === OrderItemStatus.Order &&
                        each.status === OrderItemStatus.ReadyToPrepare)
            );

        const sortingType = sorting[index];
        if (sortingType === CookColumnSort.Order) {
            return filteredAndSorted.sort((a, b) => {
                const timeA = a.orderTime ? new Date(a.orderTime).getTime() : Infinity;
                const timeB = b.orderTime ? new Date(b.orderTime).getTime() : Infinity;
                return timeA - timeB;
            });
        }
        if (sortingType === CookColumnSort.Name) {
            return filteredAndSorted.sort((a, b) => (a.name > b.name ? 1 : -1));
        }
        if (sortingType === CookColumnSort.QR) {
            return filteredAndSorted.sort((a, b) =>
                (a.tableName ?? a.qrCodeName)! > (b.tableName ?? b.qrCodeName)! ? -1 : 1
            );
        }
        if (sortingType === CookColumnSort.Course) {
            const courseMenus = menus.filter((m) => m.type === MenuType.MultiCourse);
            const sectionOrderMap = new Map<string, number>();
            courseMenus.forEach((menu) => {
                menu.sections.forEach((section, index) => {
                    const key = section.name.toLowerCase();
                    if (!sectionOrderMap.has(key)) {
                        sectionOrderMap.set(key, index);
                    } else {
                        const prev = sectionOrderMap.get(key)!;
                        if (index < prev) sectionOrderMap.set(key, index);
                    }
                });
            });

            return filteredAndSorted.sort((a, b) => {
                // --- Primary: order time ---
                const timeA = a.orderTime ? new Date(a.orderTime).getTime() : Infinity;
                const timeB = b.orderTime ? new Date(b.orderTime).getTime() : Infinity;
                if (timeA !== timeB) return timeA - timeB; // oldest first

                // --- Secondary: table grouping ---
                // Assuming tableId or tableName identifies table
                const tableA = a.tableName ?? a.qrCodeName ?? '';
                const tableB = b.tableName ?? b.qrCodeName ?? '';
                const tableCompare = tableA.localeCompare(tableB);
                if (tableCompare !== 0) return tableCompare;

                // --- Within table: course dishes first ---
                const hasCourseA = (a.courseNumber ?? 0) > 0 || !!a.courseName;
                const hasCourseB = (b.courseNumber ?? 0) > 0 || !!b.courseName;
                if (hasCourseA && !hasCourseB) return -1;
                if (hasCourseB && !hasCourseA) return 1;

                // --- Within table & both have course: follow section order ---
                const indexA = a.courseNumber
                    ? a.courseNumber!
                    : a.courseName
                    ? sectionOrderMap.get(a.courseName.toLowerCase()) ?? Infinity
                    : Infinity;
                const indexB = b.courseNumber
                    ? b.courseNumber
                    : b.courseName
                    ? sectionOrderMap.get(b.courseName.toLowerCase()) ?? Infinity
                    : Infinity;

                return indexA - indexB;
            });
        }

        return filteredAndSorted;
    }

    function getEachList(status: OrderItemStatus, passive = false) {
        let index = 0;
        switch (status) {
            case OrderItemStatus.Order:
            case OrderItemStatus.ReadyToPrepare:
                index = 0;
                break;
            case OrderItemStatus.InProgress:
                index = 1;
                break;
            case OrderItemStatus.Ready:
                index = 2;
                break;
            default:
                break;
        }
        const list = getOrdersList(status);
        return list.map((eachOrder, i) => {
            let isDifferent = false;
            const prevOrder = list[i - 1];
            const sortingType = sorting[index];
            if (prevOrder) {
                if (sortingType === CookColumnSort.Order) {
                    isDifferent = prevOrder.orderNumber !== eachOrder.orderNumber;
                }
                if (sortingType === CookColumnSort.Name) {
                    isDifferent = prevOrder.name !== eachOrder.name;
                }
                if (sortingType === CookColumnSort.QR) {
                    isDifferent = prevOrder.qrCodeName !== eachOrder.qrCodeName;
                }
                if (sortingType === CookColumnSort.Course) {
                    isDifferent = prevOrder.qrCodeName !== eachOrder.qrCodeName;
                }
            }
            return (
                <div style={{ marginTop: isDifferent ? 16 : 0 }} key={i}>
                    <SectionCard
                        orderItem={eachOrder}
                        key={`${eachOrder.uniqueId}`}
                        passive={passive}
                        changeOrderStatus={changeOrderStatus}
                        onPrintClick={handlePrintClick}
                    />
                </div>
            );
        });
    }

    function checkCanMoved(currentStatus: OrderItemStatus, newStatus: OrderItemStatus) {
        if (currentStatus === OrderItemStatus.Order && newStatus === OrderItemStatus.InProgress) {
            return true;
        }
        if (
            currentStatus === OrderItemStatus.ReadyToPrepare &&
            newStatus === OrderItemStatus.InProgress
        ) {
            return true;
        }
        if (currentStatus === OrderItemStatus.InProgress && newStatus === OrderItemStatus.Ready) {
            return true;
        }
        if (currentStatus === OrderItemStatus.Ready && newStatus === OrderItemStatus.Closed) {
            return true;
        }
        return true;
    }

    /**
     * Function will change the status of the order visually after DnD Drop
     * and will update it with API accordingly
     * @param item
     * @param row
     */
    function changeOrderStatus(item: OrderItem, row: OrderItemStatus) {
        const status = row;
        let indexOfItem = positionOrders.current.findIndex(
            (each: OrderItem) => each.uniqueId === item.uniqueId
        );
        // IF ORDER MOVED BACK
        if (!checkCanMoved(item.status, status)) {
            return;
        }
        if (indexOfItem > -1 && item.status !== status) {
            const newPositionOrders: any = [...positionOrders.current].filter(
                (each) => each.uniqueId !== item.uniqueId
            );
            const prevStatus = item.status;
            item.status = status;
            let dailyMenuUniqueId = item.dailyMenuId;

            newPositionOrders.push(item);

            // Prevents execution of onNextStatus from SectionCard from being triggered by double-clicks or double-taps,
            // which could otherwise move two tasks simultaneously.
            dispatch({ type: 'IS_ORDER_ITEM_STATUS_CHANGING_START' });
            dispatch({ type: 'GET_POSITION_ORDERS_SUCCESS', data: newPositionOrders });
            dispatch(
                ordersStore.actionCreators.changePositionOrderStatus(
                    item.orderId!,
                    {
                        itemId: item.id,
                        itemUniqueId: dailyMenuUniqueId ? '' : item.uniqueId!,
                        status,
                        dailyMenuUniqueId: dailyMenuUniqueId || '',
                    },
                    prevStatus
                )
            );
            reRender((prev) => !prev);
        }
    } // eslint-disable-line

    function onSortClick(i: number, val: CookColumnSort) {
        sorting[i] = val;
        setSorting([...sorting]);
    }

    function doneThreeDotItems() {
        return [
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => {
                    setHideDone(!hideDone);
                    localStorage.setItem(LocalStorageKeys.hideDone, JSON.stringify(!hideDone));
                }}
            >
                <ListItemIcon>
                    {hideDone ? (
                        <SettingsBackupRestoreIcon fontSize="small" color="disabled" />
                    ) : (
                        <RemoveCircleOutline fontSize="small" color="disabled" />
                    )}
                </ListItemIcon>
                <Typography variant="inherit">
                    {hideDone ? i18n.t('orders.showAll') : i18n.t('orders.clear')}
                </Typography>
            </div>,
        ];
    }

    function getColumnHeight(id: string) {
        /* const headerHeight = document.getElementById(id)?.style.height;
        const marginTop = '5px'; */

        //return `calc(90vh - ${headerHeight} - ${marginTop})`;
        return 'calc(100% - 113px)';
    }
    const setSelectedWorkAreas = (areas: CategoryDetails[]) => {
        if (areas.length === categories?.length) {
            setSelectedWorkAreas([allItem, ...(categories || [])]);
        } else {
            dispatch(categoryStore.actionCreators.setCookCategories(areas));
        }
        localStorage.setItem(LocalStorageKeys.cookCategories, JSON.stringify(areas));
    };

    function handleCategory(chosenCategory: CategoryDetails) {
        const isSelected = selectedWorkAreas.some((area) => area.id === chosenCategory.id);
        if (!isSelected && chosenCategory.id !== 'all') {
            setSelectedWorkAreas([...selectedWorkAreas, chosenCategory]);
        } else {
            setSelectedWorkAreas(
                selectedWorkAreas
                    .filter((area) => area.id !== chosenCategory.id)
                    .filter((area) => area.id !== 'all')
            );
        }

        if (chosenCategory.id === 'all') {
            if (selectedWorkAreas.length === (categories?.length || 0) + 1) {
                setSelectedWorkAreas([]);
                return;
            }
            setSelectedWorkAreas([allItem, ...(categories || [])]);
            return;
        }
    }

    useEffect(() => {
        positionOrders.current = getUniquePositionOrders();
        reRender((prev) => !prev);
    }, [positionOrdersRedux]);

    useEffect(() => {
        dispatch(ordersStore.actionCreators.getPositionOrders(Positions.COOK));
    }, [allOrdersRedux]);

    useEffect(() => {
        if (!categories) {
            dispatch(categoryStore.actionCreators.getCategoryDetails());
        }
    }, [categories]);

    useEffect(() => {
        const cachedCategories = localStorage.getItem(LocalStorageKeys.cookCategories);
        const parsedCategories: CategoryDetails[] = cachedCategories
            ? JSON.parse(cachedCategories)
            : [];
        const hasAllItem = parsedCategories.some((c) => c.id === allItem.id);
        const validParsed = parsedCategories.filter((cc) =>
            categories?.some((c) => c.id === cc.id)
        );
        const current = parsedCategories.length
            ? hasAllItem
                ? [allItem, ...(categories || [])]
                : validParsed
            : [];
        const categoriesToSet = current.length ? current : [allItem, ...(categories || [])];
        dispatch(categoryStore.actionCreators.setCookCategories(categoriesToSet));

        if (menus.length === 0) {
            dispatch(menusStore.actionCreators.getMenus());
        }
    }, []);

    // useEffect(() => {
    //     if (selectedWorkAreas.length === categories?.length) {
    //         setSelectedWorkAreas([allItem, ...(categories || [])]);
    //     }
    // }, [cachedCategories, selectedWorkAreas]);

    return (
        <div className={classes.container}>
            <OrdersListTable
                onOrderSelected={(orders) => {
                    setSelectedOrders(orders);
                    setTimeout(() => reRender((prev) => !prev), 0);
                }}
                selectedOrders={selectedOrders}
                timeOnly
                workAreas={categories}
                selectedWorkAreas={selectedWorkAreas}
                handleCategory={handleCategory}
            />
            {/* Will be removed after testing */}
            {/* <OrdersList
                onOrderSelected={(orders) => {
                    setSelectedOrders(orders);
                    setTimeout(() => reRender((prev) => !prev), 0);
                }}
            /> */}
            <div
                style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexGrow: 11,
                }}
            >
                <div
                    style={{ width: `${columnsWidth}%`, display: 'flex', flexDirection: 'column' }}
                >
                    <SectionHeader
                        title={i18n.t('orders.toDo')}
                        onSortClick={(val) => onSortClick(0, val)}
                        amount={getOrdersList(OrderItemStatus.Order).length}
                        sorting={sorting[0]}
                        containerId={'h1'}
                    />
                    <div
                        ref={drop1}
                        className={`${classes.columnContainer} ${isOver1 && classes.hoverStyle}`}
                        style={{ height: getColumnHeight('h1') }}
                    >
                        {getEachList(OrderItemStatus.Order)}
                    </div>
                </div>
                <div
                    style={{ width: `${columnsWidth}%`, display: 'flex', flexDirection: 'column' }}
                >
                    <SectionHeader
                        title={i18n.t('orders.inProgress')}
                        onSortClick={(val) => onSortClick(1, val)}
                        amount={getOrdersList(OrderItemStatus.InProgress).length}
                        sorting={sorting[1]}
                        containerId={'h2'}
                    />
                    <div
                        ref={drop2}
                        className={`${classes.columnContainer} ${isOver2 && classes.hoverStyle}`}
                        style={{ height: getColumnHeight('h2') }}
                    >
                        {getEachList(OrderItemStatus.InProgress)}
                    </div>
                </div>
                <div
                    style={{ width: `${columnsWidth}%`, display: 'flex', flexDirection: 'column' }}
                >
                    <SectionHeader
                        title={i18n.t('orders.done')}
                        onSortClick={(val) => onSortClick(2, val)}
                        amount={getOrdersList(OrderItemStatus.Ready).length}
                        sorting={sorting[2]}
                        threeDotItems={doneThreeDotItems()}
                        containerId={'h3'}
                    />
                    <div
                        ref={drop3}
                        className={`${classes.columnContainer} ${isOver3 && classes.hoverStyle}`}
                        style={{ height: getColumnHeight('h3') }}
                    >
                        {!hideDone && getEachList(OrderItemStatus.Ready, true)}
                        {!hideDone && getEachList(OrderItemStatus.Delivered, true)}
                        {!hideDone && getEachList(OrderItemStatus.Closed, true)}
                    </div>
                </div>
                <CookBoardSkeleton isLoading={!positionOrdersLoaded} />
            </div>

            <Menu
                id="daily-menu-print-menu"
                anchorEl={printAnchorEl}
                keepMounted
                open={Boolean(printItem)}
                onClose={handlePrintClose}
                anchorPosition={{ left: 200, top: 100 }}
            >
                <MenuItem onClick={() => handlePrint(PrintType.SingleItem)}>
                    {i18n.t('common.item')}
                </MenuItem>
                <MenuItem onClick={() => handlePrint(PrintType.WholeOrder)}>
                    {i18n.t('common.wholeOrder')}
                </MenuItem>
                <MenuItem onClick={() => handlePrint(PrintType.AllInWorkArea)}>
                    {i18n.t('button.allInLabel', {
                        label: categories?.find((cat) => cat.id === printItem?.categoryId)?.name,
                    })}
                </MenuItem>
                <MenuItem onClick={() => handlePrint(PrintType.AllInStatus)}>
                    {i18n.t('button.allInLabel', { label: getItemStatusText(printItem?.status) })}
                </MenuItem>
            </Menu>
        </div>
    );
}
